import { memo, useCallback, useMemo, type MouseEvent } from "react";

import { CartSpendingGitWithPurchaseSelectionView } from "@cart/CartSpendingGiftWithPurchase/CartSpendingGiftWithPurchaseSelection.view";
import { useProducts } from "@react/hooks/useProduct";
import {
  type CartSpendingBasedGiftWithPurchase,
  type CartSpendingBasedGiftWithPurchaseSettings,
} from "@tablescape/state";
import { getShopifyGlobalId } from "@tablescape/utilities";

type Props = {
  buttonTextLabel: string;
  cartLineChanging: boolean;
  checkIfGiftSelectionIsComplete: (
    eligibleSpendingThreshold: string | undefined
  ) => boolean;
  giftWithPurchaseConfig: CartSpendingBasedGiftWithPurchaseSettings;
  handleOpenGiftWithPurchaseDrawer: (
    elibigleGiftWithPurchase: CartSpendingBasedGiftWithPurchase
  ) => void;
  titleLabel: string;
};

const CartSpendingGitWithPurchaseSelectionBase = ({
  buttonTextLabel,
  cartLineChanging,
  checkIfGiftSelectionIsComplete,
  giftWithPurchaseConfig,
  handleOpenGiftWithPurchaseDrawer,
  titleLabel,
}: Props) => {
  const productGlobalIds = useMemo(
    () =>
      giftWithPurchaseConfig.pickYourOwnProductIds.map((productId: string) =>
        getShopifyGlobalId("Product", productId)
      ),
    [giftWithPurchaseConfig.pickYourOwnProductIds]
  );
  const { products } = useProducts(productGlobalIds);

  const openGiftWithPurchaseDrawer = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.preventDefault();

      handleOpenGiftWithPurchaseDrawer({
        giftItemBadgeText: giftWithPurchaseConfig.giftItemBadgeText,
        title: giftWithPurchaseConfig.title,
        products,
        spendingThreshold: giftWithPurchaseConfig.spendingThreshold,
      });
    },
    [
      giftWithPurchaseConfig.giftItemBadgeText,
      giftWithPurchaseConfig.spendingThreshold,
      giftWithPurchaseConfig.title,
      handleOpenGiftWithPurchaseDrawer,
      products,
    ]
  );

  if (
    cartLineChanging ||
    products.length === 0 ||
    checkIfGiftSelectionIsComplete(giftWithPurchaseConfig.spendingThreshold)
  )
    return null;

  return (
    <CartSpendingGitWithPurchaseSelectionView
      buttonText={giftWithPurchaseConfig.buttonText || buttonTextLabel}
      openGiftWithPurchaseDrawer={openGiftWithPurchaseDrawer}
      title={giftWithPurchaseConfig.title || titleLabel}
    />
  );
};

export const CartSpendingGitWithPurchaseSelection = memo(
  CartSpendingGitWithPurchaseSelectionBase
);
